import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useSocket } from "../contexts/SocketContext";
import { useNavigate, useParams } from "react-router-dom";
import { LikeIcon, LikeIconSolid, MinusIcon, OwnerIcon, SettingsIcon, StarIcon } from "../assets/icons";
import TextInput from "../components/shared/inputs/TextInput";
import { CharacterSkinStrings, CharacterSlugStrings, PublicGamePlayerInfo } from "../types/SharedTypes";
import formatEventLog from "../tools/formatEventLog";
import { characters, userColours } from "../assets/general";
import Modal from "../components/Modal";
import { FlyText } from "../components/game/FlyText";
import ToggleSwitch from "../components/shared/inputs/ToggleSwitch";
import EmoteWheel, { EmoteButton } from "../components/game/EmoteWheel";
import { usePreventIndex } from "../hooks/usePreventIndex";

const RoomPage = () => {

  usePreventIndex();

  const navigate = useNavigate();
  const {user, room, game, startGame, makeGameChoice, yourTurn, isDealer, players, isNextDealer, dealCards, leaveRoom, recentEventLogs, addBotToRoom, setIsCuttingDeck, currentCard, gameConfig, currentPlayer} = useSocket();

  const {roomCode} = useParams();
  const [logsVisible, setLogsVisible] = useState(false);
  const [configVisible, setConfigVisible] = useState(false);
  const [emotesVisible, setEmotesVisible] = useState(false);
  
  const logsTimeoutRef = useRef<NodeJS.Timeout>(null);
  
  useEffect(() => {
    if (!room || !roomCode) navigate('/');
  }, [room, roomCode]);

  useEffect(() => {
    setLogsVisible(true);

    if (logsTimeoutRef.current) clearTimeout(logsTimeoutRef.current);

    logsTimeoutRef.current = setTimeout(() => {
      setLogsVisible(false);
    }, 6000);
  }, [recentEventLogs]);

  const isCreator = room?.creator === user?.id;
  const hasKing = currentCard?.rank === 13;

  return (
    <>
      {configVisible && <GameConfigModal setModalVisible={setConfigVisible} />}
      {/* <RoundResultModal /> */}
      <FlyText />
      {emotesVisible && <EmoteWheel hideWheel={() => setEmotesVisible(false)} />}
      {!!game && <EmoteButton setEmotesVisible={setEmotesVisible} />}
      <div className="item-list max-width full-width center-horizontal min-width">
        {room && user && !game &&
          <div className="tile item-list no-gap overlay outline full-width">
            <p className="subheading-small weight-light center padding-small remove-padding-bottom">New Lobby</p>
            <div className="item-list small-gap bottom-border padding">
              <div className="right-item-layout">
                <p className="heading-large">{room.code}</p>
                {isCreator && 
                  <button className="icon small escape-padding" onClick={() => setConfigVisible(true)}>
                    <SettingsIcon />
                  </button>
                }
              </div>
              <p className="subheading-medium secondary">{players.length}/12 Players</p>
            </div>

            {isCreator &&
              <div className="item-row wrap padding-right-small padding-top-small justify-end">
                <div className="tag xsmall subtle">{gameConfig.lives} Lives</div>
                <div className="tag xsmall subtle">{gameConfig.turnTimer ? `${gameConfig.turnTimer/1000}s Turns` : "No Turn Timers"}</div>
              </div>
            }
            
            <div className={`padding item-list med-gap max-height-scroll-med ${isCreator ? "remove-padding-top" : ""}`}>
              {players.map((player) =>
                <PlayerDataItem key={player.userId} player={player} userId={user.id} isCreator={isCreator} />
              )}
              {isCreator &&
                <div className="item-row">
                  <div className="user-avatar small">
                    <img src={'/img/bot.jpg'} />
                  </div>
                  <button className="subtle short" onClick={addBotToRoom}>Add Bot Player</button>
                </div>
              }
            </div>
            <div className="item-list large-gap padding-small">
              <TextInput
                secondary
                id="copyJoin"
                onChange={( ) => null}
                value={`${process.env.REACT_APP_HOME_URL}/join/${roomCode}`}
                label="Invite Friends"
                copyButton
              />
            </div>
            <div className="item-list med-gap top-border border-secondary padding-small">
              <button className="subtle" onClick={leaveRoom}>
                Leave Room
              </button>
              {isCreator && 
                <button className="highlight" onClick={startGame}>
                  Start Game
                </button>
              }
            </div>
          </div>
        }
        <div className="item-list med-gap center-horizontal">
          {currentPlayer && game &&
            <>
              {currentPlayer.lives > 0 ? (
                <div className="item-row">
                  {Array(gameConfig.lives > 6 ? 6 : gameConfig.lives).fill(1).map((_,i) => 
                    <div className="icon-display small red" key={i}>
                      {currentPlayer.lives > i ? <LikeIconSolid /> : <LikeIcon />}
                    </div>
                  )}
                  {currentPlayer.lives > 6 && 
                    <div className="tile white padding-xxs">
                      <p className="heading-small">+{currentPlayer.lives - 6}</p>
                    </div>
                  }
                </div>
              ) : (
                <p className="heading-medium">☠️</p>
              )}
            
              {yourTurn && (game?.currentState === 'player_decision' || game?.currentState === 'dealer_decision') &&
                <div className="split-button" data-single-option={hasKing}>
                  {isDealer && !hasKing && <button className="empty" onClick={() => setIsCuttingDeck(true)}>Cut Deck</button>}
                  {!isDealer && !hasKing && <button className="empty" onClick={() => makeGameChoice('swap')}>Swap Cards</button>}
                  {!hasKing && <div className="divider-line-vertical full-height" />}
                  <button className="empty" onClick={() => makeGameChoice('stay')}>Stay</button>
                </div>
              }
            </>
          }
        </div>
        {isNextDealer && game?.currentState === 'round_waiting' &&
          <button className="primary" onClick={dealCards}>Deal Next Round</button>
        }
        <div className="event-log" aria-hidden={!logsVisible}>
          <div className="item-list padding center-horizontal small-gap">
            {recentEventLogs.map((event, i) => 
              <p key={event.time.toString() + i.toString()} className="body">{formatEventLog(event, true)}</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const PlayerDataItem = ({player, userId, showDiag, isCreator}: {
  player: PublicGamePlayerInfo,
  userId: string,
  showDiag?: boolean,
  isCreator: boolean
}) => {

  const {kickPlayer} = useSocket();

  const isRealBot = !player.isDisconnected && player.isBot;
  const imgSrc = (slug: CharacterSlugStrings, skin: CharacterSkinStrings) => isRealBot 
    ? '/img/bot.jpg'
    : `/img/characters/${characters[slug].name}-${skin}.png`;

  return (
    <div
      className="right-item-layout min-width"
      style={{order: player.isCreator ? "-2" : player.userId === userId ? "-1" : undefined}}
    >
      <div className="item-row" aria-disabled={player.isDisconnected && !player.isBot}>
        <div className={"user-avatar small"} data-bot={player.isDisconnected && player.isBot}>
          {isRealBot && <div className="overlay" style={{backgroundColor: userColours[player.colour]}} />}
          <img src={imgSrc(player.character.slug, player.character.skin)} />
        </div>
        <p className="body ellipsis">{player.name} {player.userId === userId ? <span className="body secondary"> (You)</span> : ''}</p>
        {showDiag && <p className="body">Position: {player.position}</p>}
        {showDiag && <p className="body">Lives: {player.lives}</p>}
        {player.card && showDiag && <p className="body">Card: {player.card.rank}{player.card.suit}</p>}
        {player.wins > 0 &&
          <div className="item-row small-gap" title={`${player.wins} Win(s)`}>
            <div className="icon-display xsmall push-up">
              <StarIcon />
            </div>
            <p className="subheading-medium weight-bold">{player.wins}</p>
          </div>
        }
        {player.isCreator && 
          <div className="icon-display xsmall push-up" title="Room Creator">
            <OwnerIcon />
          </div>
        }
      </div>
      {isCreator && player.userId !== userId &&
        <button className="icon xxs background cancel" onClick={() => kickPlayer(player.userId)}>
          <MinusIcon />
        </button>
      }
    </div>
  )
}

const GameConfigModal = ({setModalVisible}: {setModalVisible: (prev: boolean) => void}) => {

  const {gameConfig, setGameConfig} = useSocket();

  const [lives, setLives] = useState((gameConfig.lives).toString());
  const [timer, setTimer] = useState((gameConfig.turnTimer/1000).toString());

  const handleNumberAsString = (val: string, maxLength: number, setter: Dispatch<SetStateAction<string>>)=> {
    const value = Number(val);
    if (val === '') {
      setter(val);
      return;
    }
    if (Object.is(value, NaN) || value === 0) return;
    
    const maxValue = maxLength;
    
    if (value > maxValue) return;
    
    setter(value.toString());
  }

  const updateLives = (val: string) => {
    handleNumberAsString(val, 99, setLives);
  }
  const updateTimer = (val: string) => {
    handleNumberAsString(val, 60, setTimer);
  }
  
  const handleToggleTimer = (switched: boolean) => {
    setTimer((switched ? (gameConfig.turnTimer ? gameConfig.turnTimer/1000 : 30) : 0).toString())
  }

  const handleCancel = () => {
    setModalVisible(false);
  }
  const handleSave = () => {
    setGameConfig(prev => ({...prev, lives: Number(lives), turnTimer: Number(timer)*1000}));
    setModalVisible(false);
  }

  return (
    <Modal
      setModalVisible={setModalVisible} 
      classNames="auto-height narrow"
      fixedBottomItem
      fixedBottomItemChildren={
        <div className="item-row padding-small">
          <button onClick={handleCancel} className="subtle full-width">Cancel</button>
          <button onClick={handleSave} className="primary full-width">Save</button>
        </div>
      }
      removePadding
      modalTitle={"Game Settings"}
      controls={true}
      children={
        <div className="padding padding-top-small item-list large-gap">
          <TextInput
            id={"livesinput"}
            value={lives}
            onChange={updateLives}
            label={"Number of Lives"}
            placeholder={"3"}
          />
          <ToggleSwitch
            initialValue={gameConfig.turnTimer !== 0}
            label={"Turn Timer"}
            id={'timerswitch'}
            handleChange={handleToggleTimer}
          />
          <TextInput
            id={"timerinput"}
            value={timer}
            onChange={updateTimer}
            label={"Turn Timer (Seconds)"}
            placeholder={"30 (seconds)"}
            disabled={timer === "0"}
            note={"Player's turn will be taken for them if timer ends."}
          />
        </div>
      }
    />
  )
}

export default RoomPage;